import { Injectable } from '@angular/core';
import { LgvAuthMgrService } from './lgv-auth-mgr.service';
import { LgvAuth0Service } from './lgv-auth0.service';
import { LgvClientAuthService } from './lgv-client-auth.service';
import { LgvUserService } from './lgv-user.service';
import { combineLatest } from 'rxjs';
import { filter, distinctUntilChanged } from 'rxjs/operators';
import posthog from 'posthog-js';

@Injectable({
  providedIn: 'root'
})
export class LgvAnalyticsService {

  constructor(
    private authMgr: LgvAuthMgrService,
    private auth0Service: LgvAuth0Service,
    private clientAuthService: LgvClientAuthService,
    private userService: LgvUserService
  ) {
    this.initializeAnalytics();
  }

  private initializeAnalytics() {
    // Listen to authentication state changes
    this.authMgr.AuthState$.pipe(
      distinctUntilChanged()
    ).subscribe(authState => {
      this.handleAuthStateChange(authState);
    });

    // Listen to user profile changes for Auth0 users
    this.authMgr.Auth0UserInfo$.pipe(
      filter(userInfo => !!userInfo)
    ).subscribe(userInfo => {
      this.identifyAuth0User(userInfo);
    });

    // Listen to database user snapshot for Auth0 users to get the actual userId
    this.userService.DBUserSS$.pipe(
      filter(userSS => !!userSS && !!userSS.agentInfo)
    ).subscribe(userSS => {
      this.updateAuth0UserWithDatabaseInfo(userSS);
    });

    // Listen to user roles changes from LgvUserService
    this.userService.DBUserRoles$.pipe(
      filter(roles => !!roles && roles.length > 0)
    ).subscribe(roles => {
      this.updateUserRoles(roles);
    });

    // Listen to client info changes for client auth
    this.clientAuthService.ClientInfo$.pipe(
      filter(clientInfo => !!clientInfo)
    ).subscribe(clientInfo => {
      this.identifyClient(clientInfo);
    });
  }

  private handleAuthStateChange(authState: string) {
    switch (authState) {
      case 'Auth0Auth':
        this.setSessionProperties('auth0_user');
        break;
      case 'ClientAuth':
        this.setSessionProperties('client_user');
        break;
      case 'SecureClientAuth':
        this.setSessionProperties('secure_client_user');
        break;
      case 'None':
        this.handleLogout();
        break;
    }
  }

  private updateUserRoles(roles: string[]) {
    console.log('PostHog: Updating user roles from LgvUserService:', roles);
    
    // Update user properties with roles
    posthog.setPersonProperties({
      roles: roles
    });

    // Update session properties with roles
    posthog.register({
      'user_roles': roles
    });

    this.trackEvent('user_roles_updated', {
      roles: roles,
      roles_count: roles.length,
      source: 'lgv_user_service'
    });
  }

  private identifyAuth0User(userInfo: any) {
    const userId = userInfo.sub || userInfo.email;
    
    // Identify the user in PostHog with basic info
    posthog.identify(userId, {
      email: userInfo.email,
      name: userInfo.name,
      given_name: userInfo.given_name,
      family_name: userInfo.family_name,
      email_verified: userInfo.email_verified,
      auth_type: 'auth0',
      tenant: this.authMgr.CurrentAuth0Tenant
    });

    // Set user-level properties for this session
    posthog.register({
      'user_type': 'auth0_user',
      'auth_tenant': this.authMgr.CurrentAuth0Tenant,
      'email_verified': userInfo.email_verified
    });

    // Roles will be handled separately by the DBUserRoles$ subscription
    console.log('PostHog: Auth0 user identified, roles will be updated via LgvUserService');

    this.trackEvent('user_authenticated', {
      auth_type: 'auth0',
      email_verified: userInfo.email_verified,
      tenant: this.authMgr.CurrentAuth0Tenant
    });
  }

  private identifyClient(clientInfo: any) {
    const userId = `client_${clientInfo.id}`;
    const clientId = clientInfo.id?.toString();
    
    console.log('PostHog: Identifying client user, clientId:', clientId);
    
    // Identify the client user in PostHog
    posthog.identify(userId, {
      email: clientInfo.email,
      firstName: clientInfo.firstName,
      lastName: clientInfo.lastName,
      clientId: clientId,
      auth_type: 'client_bearer_token',
      account_type: 'client'
    });

    // Group this user under their client organization
    if (clientId) {
      posthog.group('client', clientId, {
        client_name: `${clientInfo.firstName} ${clientInfo.lastName}`,
        client_email: clientInfo.email,
        client_id: clientId
      });
    }

    // Set session properties
    posthog.register({
      'user_type': 'client',
      'client_id': clientId,
      'auth_type': this.authMgr.IsSecureClient ? 'secure_client' : 'client'
    });

    this.trackEvent('client_authenticated', {
      auth_type: this.authMgr.IsSecureClient ? 'secure_client' : 'client',
      client_id: clientId
    });
  }

  private setSessionProperties(userType: string) {
    posthog.register({
      'session_auth_state': userType,
      'timestamp': new Date().toISOString()
    });
  }

  private handleLogout() {
    this.trackEvent('user_logout', {
      previous_auth_state: posthog.get_property('session_auth_state')
    });
    
    // Reset PostHog user identity
    posthog.reset();
  }

  // Public methods for tracking events throughout the application
  public trackEvent(eventName: string, properties: any = {}) {
    const enrichedProperties = {
      ...properties,
      auth_state: this.authMgr.AuthState$.value,
      is_authenticated: this.getIsAuthenticated(),
      user_type: this.getUserType(),
      user_id: this.getUserId(),
      client_id: this.getClientId(),
      timestamp: new Date().toISOString()
    };

    posthog.capture(eventName, enrichedProperties);
  }

  public trackPageView(pageName: string, additionalProperties: any = {}) {
    this.trackEvent('page_viewed', {
      page_name: pageName,
      url: window.location.pathname,
      ...additionalProperties
    });
  }

  public trackFeatureUsage(featureName: string, properties: any = {}) {
    this.trackEvent('feature_used', {
      feature_name: featureName,
      ...properties
    });
  }

  public trackBusinessEvent(eventName: string, properties: any = {}) {
    const businessProperties = {
      ...properties,
      event_category: 'business',
      user_id: this.getUserId(),
      client_id: this.getClientId(),
      user_role: this.getUserRole()
    };

    this.trackEvent(eventName, businessProperties);
  }

  public setUserProperty(key: string, value: any) {
    posthog.setPersonProperties({ [key]: value });
  }

  public refreshUserRoles() {
    if (this.authMgr.IsAuth0) {
      console.log('PostHog: Refreshing user roles from LgvUserService...');
      
      // Get current roles from user service
      const currentRoles = this.userService.DBUserRoles;
      
      if (currentRoles && currentRoles.length > 0) {
        this.updateUserRoles(currentRoles);
        console.log('PostHog: Successfully refreshed user roles:', currentRoles);
      } else {
        console.log('PostHog: No roles found in LgvUserService');
        this.trackEvent('user_roles_empty', {
          auth_state: this.authMgr.AuthState$.value,
          user_email: this.authMgr.Auth0UserInfo$.value?.email,
          source: 'lgv_user_service'
        });
      }
    }
  }

  public trackError(error: any, context: string = '') {
    this.trackEvent('error_occurred', {
      error_message: error.message || error.toString(),
      error_context: context,
      user_agent: navigator.userAgent,
      url: window.location.pathname
    });
  }

  // Helper methods
  private getUserType(): string {
    if (this.authMgr.IsAuth0) return 'auth0_user';
    if (this.authMgr.IsSecureClient) return 'secure_client';
    if (this.authMgr.IsClient) return 'client';
    return 'anonymous';
  }

  private getIsAuthenticated(): boolean {
    return this.authMgr.IsAuth0 || this.authMgr.IsClient;
  }

  private getUserId(): string | null {
    // For Auth0 users, get the database user ID
    if (this.authMgr.IsAuth0 && this.userService.DBUserSS$.value?.agentInfo?.id) {
      return this.userService.DBUserSS$.value.agentInfo.id;
    }
    
    // For client users, we can also return the client ID as user ID
    if (this.authMgr.IsClient && this.authMgr.ClientInfo?.id) {
      return `client_${this.authMgr.ClientInfo.id}`;
    }
    
    return null;
  }

  private getClientId(): string | null {
    if (this.authMgr.IsClient && this.authMgr.ClientInfo) {
      return this.authMgr.ClientInfo.id?.toString() || null;
    }
    return null;
  }

  private getUserRole(): string[] {
    if (this.authMgr.IsAuth0) {
      // Try to get roles from PostHog session properties first
      const sessionRoles = posthog.get_property('user_roles');
      if (sessionRoles && Array.isArray(sessionRoles) && sessionRoles.length > 0) {
        return sessionRoles;
      }
      
      // Fallback: get from LgvUserService if available
      const userServiceRoles = this.userService.DBUserRoles;
      if (userServiceRoles && userServiceRoles.length > 0) {
        return userServiceRoles;
      }
      
      return [];
    }
    if (this.authMgr.IsClient) {
      return ['client'];
    }
    return [];
  }

  // Convenience methods for specific business events
  public trackLoanEvent(eventName: string, loanData: any) {
    this.trackBusinessEvent(`loan_${eventName}`, {
      loan_amount: loanData.amount,
      loan_type: loanData.type,
      application_stage: loanData.stage
    });
  }

  public trackLoanApplication(action: 'start' | 'complete', properties: any = {}) {
    this.trackBusinessEvent(`loan_application_${action}`, {
      ...properties,
      application_action: action
    });
  }

  public trackDashboardView(dashboardType: string) {
    this.trackFeatureUsage('dashboard_viewed', {
      dashboard_type: dashboardType
    });
  }

  public trackClientInteraction(interactionType: string, clientData: any = {}) {
    this.trackBusinessEvent(`client_${interactionType}`, {
      target_client_id: clientData.clientId,
      interaction_context: clientData.context
    });
  }

  private updateAuth0UserWithDatabaseInfo(userSS: any) {
    console.log('PostHog: Updating Auth0 user with database info, userId:', userSS.agentInfo.id);
    
    // Update user properties with database user ID and additional info
    posthog.setPersonProperties({
      userId: userSS.agentInfo.id,
      firstName: userSS.agentInfo.firstName,
      lastName: userSS.agentInfo.lastName,
      companyId: userSS.company?.id,
      companyName: userSS.company?.name,
      organizationId: userSS.organization?.id,
      organizationName: userSS.organization?.name
    });

    // Update session properties
    posthog.register({
      'user_id': userSS.agentInfo.id,
      'company_id': userSS.company?.id,
      'company_name': userSS.company?.name,
      'organization_id': userSS.organization?.id
    });

    this.trackEvent('user_database_info_updated', {
      user_id: userSS.agentInfo.id,
      company_id: userSS.company?.id,
      company_name: userSS.company?.name,
      has_organization: !!userSS.organization
    });
  }

  // Debugging and utility methods
  public getCurrentTrackingInfo(): any {
    return {
      user_id: this.getUserId(),
      client_id: this.getClientId(),
      user_type: this.getUserType(),
      auth_state: this.authMgr.AuthState$.value,
      is_authenticated: this.getIsAuthenticated(),
      user_roles: this.getUserRole(),
      posthog_distinct_id: posthog.get_distinct_id(),
      posthog_session_props: {
        user_id: posthog.get_property('user_id'),
        client_id: posthog.get_property('client_id'),
        user_roles: posthog.get_property('user_roles'),
        auth_state: posthog.get_property('session_auth_state')
      }
    };
  }

  public logCurrentTrackingInfo() {
    console.log('PostHog Current Tracking Info:', this.getCurrentTrackingInfo());
  }
} 